'use client';

import Link from 'next/link';
import { trackCtaClick } from '@/lib/analytics';
import { buildContactUrl, type ContactSource } from '@/lib/utils/contact-url';

/**
 * Contextual Service CTA Component
 *
 * Displays a call-to-action that links to a specific service,
 * designed to convert content readers into service inquiries.
 *
 * Variants:
 * - banner: Full-width gradient banner (default, like CTASection)
 * - card: Compact card format for sidebars or inline use
 * - inline: Minimal inline format for within content
 */

export interface ContextualServiceCTAProps {
  /** Title of the CTA */
  title: string;
  /** Description text */
  description: string;
  /** Primary service to link to */
  serviceSlug: string;
  /** Display name of the service */
  serviceName: string;
  /** Optional secondary service */
  secondaryServiceSlug?: string;
  secondaryServiceName?: string;
  /** Visual variant */
  variant?: 'banner' | 'card' | 'inline';
  /** Custom primary button text (defaults to "Learn More") */
  primaryButtonText?: string;
  /** Custom secondary button text (defaults to "Talk to an Expert") */
  secondaryButtonText?: string;
  /** Analytics location identifier */
  ctaLocation?: 'kb_article' | 'blog_post' | 'tool_page' | 'inline';
  /** Contact URL source type for personalization */
  contactSource?: ContactSource;
  /** Content slug for contact URL personalization */
  contentSlug?: string;
  /** Content category for contact URL personalization */
  contentCategory?: string;
}

export default function ContextualServiceCTA({
  title,
  description,
  serviceSlug,
  serviceName,
  secondaryServiceSlug,
  secondaryServiceName,
  variant = 'banner',
  primaryButtonText,
  secondaryButtonText = 'Talk to an Expert',
  ctaLocation = 'inline',
  contactSource,
  contentSlug,
  contentCategory,
}: ContextualServiceCTAProps) {
  const primaryHref = `/services/${serviceSlug}`;

  // Build personalized contact URL when source info is provided
  const contactUrl = contactSource && contentSlug
    ? buildContactUrl({
        src: contactSource,
        id: contentSlug,
        cat: contentCategory,
      })
    : '/contact?ref=contextual-cta';

  const secondaryHref = secondaryServiceSlug
    ? `/services/${secondaryServiceSlug}`
    : contactUrl;

  const finalPrimaryText = primaryButtonText || `Learn About ${serviceName}`;
  const finalSecondaryText = secondaryServiceName
    ? `Explore ${secondaryServiceName}`
    : secondaryButtonText;

  const handlePrimaryClick = () => {
    trackCtaClick(finalPrimaryText, ctaLocation as 'inline', primaryHref);
  };

  const handleSecondaryClick = () => {
    trackCtaClick(finalSecondaryText, ctaLocation as 'inline', secondaryHref);
  };

  // Banner variant - full-width gradient like CTASection
  if (variant === 'banner') {
    return (
      <section className="bg-gradient-to-br from-[#2cc3d1] to-[#2813e8] text-white py-12 px-4 rounded-lg">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            {title}
          </h3>
          <p className="text-lg text-white/90 mb-6">
            {description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href={primaryHref}
              className="inline-flex items-center justify-center min-h-[44px] bg-white text-[#2813e8] hover:bg-gray-100 px-6 py-3 rounded-md font-semibold transition-colors shadow-lg"
              onClick={handlePrimaryClick}
            >
              {finalPrimaryText}
            </Link>
            <Link
              href={secondaryHref}
              className="inline-flex items-center justify-center min-h-[44px] bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#2813e8] px-6 py-3 rounded-md font-semibold transition-colors"
              onClick={handleSecondaryClick}
            >
              {finalSecondaryText}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Card variant - compact for sidebars or smaller spaces
  if (variant === 'card') {
    return (
      <div className="bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] text-white rounded-lg p-4 shadow-lg">
        <h4 className="text-lg font-bold mb-1.5">{title}</h4>
        <p className="text-white/90 text-xs mb-3 leading-relaxed">{description}</p>
        <div className="flex gap-2">
          <Link
            href={primaryHref}
            className="flex-1 text-center bg-white text-[#2813e8] hover:bg-gray-100 px-3 py-1.5 rounded font-semibold text-xs transition-colors"
            onClick={handlePrimaryClick}
          >
            {finalPrimaryText}
          </Link>
          <Link
            href={secondaryHref}
            className="flex-1 text-center bg-transparent border border-white/50 text-white hover:bg-white/10 px-3 py-1.5 rounded font-medium text-xs transition-colors"
            onClick={handleSecondaryClick}
          >
            {finalSecondaryText}
          </Link>
        </div>
      </div>
    );
  }

  // Inline variant - minimal for within content flow
  return (
    <div className="my-8 p-6 bg-blue-50 border-l-4 border-[#2813e8] rounded-r-lg">
      <h4 className="text-lg font-bold text-[#4B4F58] mb-2">{title}</h4>
      <p className="text-gray-600 mb-4">{description}</p>
      <div className="flex flex-wrap gap-3">
        <Link
          href={primaryHref}
          className="inline-flex items-center text-[#2813e8] font-semibold hover:underline"
          onClick={handlePrimaryClick}
        >
          {finalPrimaryText}
          <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
        <span className="text-gray-600">|</span>
        <Link
          href={secondaryHref}
          className="inline-flex items-center text-gray-600 hover:text-[#2813e8] font-medium"
          onClick={handleSecondaryClick}
        >
          {finalSecondaryText}
        </Link>
      </div>
    </div>
  );
}
